import { Component, ElementRef, HostListener, OnDestroy, OnInit, Type } from '@angular/core';
import { CdkDragEnd } from '@angular/cdk/drag-drop';
import { Subject } from 'rxjs';
import { filter, skip, takeUntil } from 'rxjs/operators';

import { ActionMonitorRun } from '../../models/action-monitor.model';
import { actionMonitorHostFor, isFloatRun, xactFloatPillLabel } from '../../services/action-monitor-host';
import { ActionMonitorService } from '../../services/action-monitor.service';
import { XactFloatService } from '../../services/xact-float.service';
import { XactFloatState } from '../../services/xact-float-state';

/** Where an undragged window sits — one object, so change detection never hands cdkDrag a "new" position. */
const DRAG_ORIGIN: Readonly<{ x: number; y: number }> = Object.freeze({ x: 0, y: 0 });

/** How close to the viewport's edge the window may sit. */
const VIEWPORT_MARGIN_PX = 8;

/**
 * THE FLOATING XACT PLAYGROUND (XCT-E-12-02) — write an Xact script, validate, compile and execute it on
 * whatever page is open, without going to /playground/xact.
 *
 * Mounted ONCE at the app shell, outside the router outlet: the run this window starts navigates the tab, and the
 * window has to be there when it lands. It
 * sits at the monitor's z-index, above the run overlay and above dialogs, so it stays usable while a run drives
 * the page underneath.
 *
 * THIS IS ONLY THE CHROME. The body — the editor, CodeMirror, the Playground link — is a lazy chunk loaded the
 * first time the window opens, so a session that never opens it never downloads the editor. Once loaded it stays
 * mounted while the window is only MINIMISED (the pill hides it, the work in it survives); closing the window
 * drops it, and the draft is what brings the script back.
 *
 * IT IS THE DEVELOPER'S EDITOR, NOT THE RUN WINDOW (XAT-E-377-05, owner's review 2026-09-27). The script writer is
 * for developers; a run's log, and the conversation a chat's run came from, are for every user — so they are not one
 * component. This window shows a run only as the drawer under the script, and only a run IT started, while it is up;
 * every other run — a chat's, the Playground page's, AutoPilot's — is the Action Monitor's (`actionMonitorHostFor`).
 * This replaced the one-host rule of the 2026-09-26 review, under which this window showed every run.
 */
@Component({
  standalone: false,
  selector: 'app-xact-float',
  templateUrl: './xact-float.component.html',
  styleUrls: ['./xact-float.component.scss']
})
export class XactFloatComponent implements OnInit, OnDestroy {
  state: XactFloatState;

  /** The one run `ActionMonitorService` holds, whoever started it — shown here only when it is this window's. */
  run: ActionMonitorRun | null = null;

  /** The Action Monitor's own dock tab is on the bottom edge (a run it shows, folded) — this window's tab moves aside. */
  isMonitorDocked = false;

  /** Whether the Action Monitor is folded — with the host, what says its tab is showing. */
  private monitorCollapsed = true;

  /** The run drawer folded down to its title row — the reader's choice, reset when a new run starts. */
  isDrawerFolded = false;

  /** The lazily loaded body, or null until the window first opens. */
  bodyComponent: Type<unknown> | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly floatService: XactFloatService,
    private readonly monitor: ActionMonitorService,
    private readonly host: ElementRef<HTMLElement>
  ) {
    this.state = floatService.state;
  }

  ngOnInit(): void {
    this.floatService.state$.pipe(takeUntil(this.destroy$)).subscribe(state => {
      this.state = state;
      this.syncHost();
      if (state.mode !== 'closed') this.loadBody();
      // After the render this state causes — the window's size is only known once it is drawn.
      if (state.mode !== 'closed') setTimeout(() => this.keepInView());
    });

    this.monitor.run$.pipe(takeUntil(this.destroy$)).subscribe(run => {
      const isNewRun = run?.status === 'running' && run.runId !== this.run?.runId;

      // A NEW RUN OPENS THE DRAWER even if the last one was folded away: the point of pressing Run is to watch it.
      if (isNewRun && isFloatRun(run)) this.isDrawerFolded = false;

      this.run = run;
      this.syncHost();
      this.syncMonitorDock();
    });

    // VALIDATE WANTS THE SCRIPT IN VIEW: the drawer folds to its title row, and the run is still one click away.
    this.floatService.foldRun$.pipe(takeUntil(this.destroy$)).subscribe(() => (this.isDrawerFolded = true));

    this.monitor.collapsed$.pipe(takeUntil(this.destroy$)).subscribe(collapsed => {
      this.monitorCollapsed = collapsed;
      this.syncMonitorDock();
    });

    // SOMEBODY ASKED TO SEE THE RUN — a result being shown. The Action Monitor expands its panel for a run it shows;
    // for this window's own run, that means bringing the window back from its pill.
    this.monitor.collapsed$
      .pipe(skip(1), filter(collapsed => !collapsed), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.hostsRun && this.state.mode === 'minimised') this.floatService.restore();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isVisible(): boolean { return this.state.mode !== 'closed'; }
  get isMinimised(): boolean { return this.state.mode === 'minimised'; }
  get isLarge(): boolean { return this.state.size === 'large'; }
  // One object, never a fresh literal: a new one each check is a new input to cdkDrag, which resets it mid-drag.
  get dragPosition(): { x: number; y: number } { return this.state.position ?? DRAG_ORIGIN; }
  get isRunning(): boolean { return this.run?.status === 'running'; }

  /** The run on hand is this window's own — the only kind it shows (XAT-E-377-05). */
  get hostsRun(): boolean { return isFloatRun(this.run); }

  get pillLabel(): string {
    return xactFloatPillLabel(this.hostsRun ? this.run : null);
  }

  minimise(): void { this.floatService.minimise(); }
  restore(): void { this.floatService.restore(); }
  toggleSize(): void { this.floatService.toggleSize(); }
  close(): void { this.floatService.close(); }

  toggleDrawer(): void { this.isDrawerFolded = !this.isDrawerFolded; }

  /** Put a finished run away — the Action Monitor's Close, from inside the window. */
  dismissRun(): void { this.monitor.close(); }

  onDragEnded(event: CdkDragEnd): void {
    this.floatService.moved(event.source.getFreeDragPosition());
  }

  /**
   * NEVER OFF THE SCREEN. cdkDrag keeps a drag inside the page, but a window dropped low and then made larger, or
   * restored into a shorter browser, reaches past the viewport — with its footer and Execute out of reach. So after
   * every change, and on a resize, the dropped offset is pulled back just far enough for the whole window to show.
   */
  @HostListener('window:resize')
  keepInView(): void {
    const element = this.host.nativeElement.querySelector<HTMLElement>('#xact-float');
    if (!element || this.state.mode === 'closed') return;

    const rect = element.getBoundingClientRect();
    const position = this.dragPosition;
    let dx = 0;
    let dy = 0;

    if (rect.bottom > window.innerHeight - VIEWPORT_MARGIN_PX) dy = window.innerHeight - VIEWPORT_MARGIN_PX - rect.bottom;
    if (rect.top + dy < VIEWPORT_MARGIN_PX) dy = VIEWPORT_MARGIN_PX - rect.top;
    if (rect.left < VIEWPORT_MARGIN_PX) dx = VIEWPORT_MARGIN_PX - rect.left;
    if (rect.right + dx > window.innerWidth - VIEWPORT_MARGIN_PX) dx = window.innerWidth - VIEWPORT_MARGIN_PX - rect.right;

    if (dx || dy) this.floatService.moved({ x: position.x + dx, y: position.y + dy });
  }

  /** Tell the Action Monitor whether this window is showing the run on hand (XAT-E-377-05, `actionMonitorHostFor`). */
  private syncHost(): void {
    this.monitor.setHost(actionMonitorHostFor(this.state.mode, this.hostsRun));
  }

  /**
   * TWO TABS, ONE EDGE (XAT-E-377-05). The Action Monitor docks its folded run window at the same spot this window docks
   * its own; while that tab is showing — a run the Action Monitor shows, folded — this window's tab sits to its left.
   */
  private syncMonitorDock(): void {
    this.isMonitorDocked = !!this.run && !this.hostsRun && this.monitorCollapsed;
  }

  private loadBody(): void {
    if (this.bodyComponent) return;

    import('./xact-float-body/xact-float-body.component').then(m => (this.bodyComponent = m.XactFloatBodyComponent));
  }
}
